import {
   useFloating, flip, shift, autoUpdate, useListItem,
   offset, size, useListNavigation, useTypeahead, useClick, useDismiss,
   useRole, useInteractions, FloatingFocusManager, FloatingList
} from "@floating-ui/react";
import { Localization } from "@viamap/viamap2-common";
import { Reorder, useDragControls } from "motion/react";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { MITFloatingPortal } from "src/componentsUtils/PortalHelpers";
import { GlassInputControl } from "./GlassInput";

type OptionValue = string | number | undefined;
const emptySelection: never[] = [];

export type AdvOptionProps<T extends OptionValue> = {
   label: string,
   value: T,
   disabled?: boolean,
   children?: React.ReactNode
};

export type GlassMultiSelectProps<T extends OptionValue> = {
   children: ReactElement<AdvOptionProps<T>> | Array<ReactElement<AdvOptionProps<T>>>,
   value?: T[],
   onChange?: (value: T[]) => void,
   placeholder?: string,
   disabled?: boolean,
   "aria-label"?: string
};

function optionKey(value: OptionValue) {
   return `${typeof value}:${String(value)}`;
}

/** Controlled selection; value order is display order. Disabled options cannot be added or removed, including by bulk actions. */
export function GlassMultiSelect<T extends OptionValue>({
   children, value = emptySelection, onChange, placeholder, disabled = false, "aria-label": ariaLabel
}: GlassMultiSelectProps<T>) {
   const [isOpen, setIsOpen] = useState(false);
   const [activeIndex, setActiveIndex] = useState<number | null>(null);
   const triggerRef = useRef<HTMLButtonElement | null>(null);
   const elementsRef = useRef<Array<HTMLElement | null>>([]);
   const labelsRef = useRef<Array<string | null>>([]);
   const options = React.Children.toArray(children).filter(
      (child): child is ReactElement<AdvOptionProps<T>> => React.isValidElement<AdvOptionProps<T>>(child)
   );
   const availableOptions = options.filter((option) => !value.includes(option.props.value));
   const selectableOptions = availableOptions.filter((option) => !option.props.disabled);
   const removableValues = value.filter((val) => !options.find((option) => option.props.value === val)?.props.disabled);
   const label = ariaLabel ?? placeholder ?? Localization.getText("Select options");

   const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-start",
      open: isOpen && !disabled,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
         offset(4), flip(), shift({ padding: 8 }),
         size({
            padding: 8,
            apply({ rects, elements, availableHeight, availableWidth }) {
               Object.assign(elements.floating.style, {
                  minWidth: `${Math.min(rects.reference.width, availableWidth)}px`,
                  maxWidth: `${availableWidth}px`,
                  maxHeight: `${Math.max(0, availableHeight)}px`
               });
            }
         })
      ]
   });

   useEffect(() => {
      if (disabled) setIsOpen(false);
   }, [disabled]);

   useEffect(() => {
      setActiveIndex(null);
   }, [value, children]);

   const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      onNavigate: setActiveIndex,
      disabledIndices: availableOptions.flatMap((option, index) => option.props.disabled ? [index] : [])
   });
   const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      activeIndex,
      enabled: isOpen && !disabled,
      onMatch: setActiveIndex
   });
   const click = useClick(context, { enabled: !disabled });
   const dismiss = useDismiss(context);
   const role = useRole(context, { role: "dialog" });
   const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, typeahead, click, dismiss, role]);

   function commit(nextValue: T[]) {
      if (disabled) return;
      onChange?.(nextValue);
   }

   function handleSelect(option: AdvOptionProps<T>) {
      if (option.disabled || value.includes(option.value)) return;
      commit([...value, option.value]);
      // The selected option leaves the list, so return focus to a stable element.
      setActiveIndex(null);
      triggerRef.current?.focus();
   }

   function handleDeselect(val: T) {
      commit(value.filter((item) => item !== val));
      triggerRef.current?.focus();
   }

   return (
      <>
         <div className="GlassSelect Multi" ref={refs.setPositionReference} aria-disabled={disabled} onKeyDown={(event) => {
            if (event.key === "Enter") event.stopPropagation();
         }}>
            <Reorder.Group as="div" axis="y" values={value} onReorder={commit} className="MultiElementsContainer" aria-label={label}>
               {value.map((val) => {
                  const option = options.find((item) => item.props.value === val);
                  return (
                     <SelectedOption
                        key={optionKey(val)}
                        value={val}
                        label={option?.props.label ?? String(val)}
                        disabled={disabled || Boolean(option?.props.disabled)}
                        onRemove={() => handleDeselect(val)}
                     >
                        {option ?? <span className="AdvOption">{String(val)}</span>}
                     </SelectedOption>
                  );
               })}
            </Reorder.Group>
            <button
               type="button"
               className="MultiSelectTrigger"
               ref={(node) => { triggerRef.current = node; refs.setReference(node); }}
               disabled={disabled}
               {...getReferenceProps({ "aria-label": label })}
            >
               <span className={value.length ? "" : "placeholder"}>
                  {value.length ? Localization.getText("Add") : placeholder ?? Localization.getText("Select options")}
               </span>
               <MIBIcon icon="expandDown" />
            </button>
            {isOpen && !disabled && (
               <MITFloatingPortal>
                  <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
                     <div
                        className="GlassSelect-options MultiSelectOptions"
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps({ "aria-label": label })}
                     >
                        <div className="MultiSelectActions">
                           <button type="button" disabled={!selectableOptions.length} onClick={() => {
                              commit([...value, ...selectableOptions.map((option) => option.props.value)]);
                              triggerRef.current?.focus();
                           }}>{Localization.getText("Select all")}</button>
                           <button type="button" disabled={!removableValues.length} onClick={() => {
                              commit(value.filter((val) => !removableValues.includes(val)));
                              triggerRef.current?.focus();
                           }}>{Localization.getText("Clear all")}</button>
                        </div>
                        <div role="listbox" aria-label={label} aria-multiselectable="true" className="MultiSelectList">
                           <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                              {availableOptions.map((option, index) => (
                                 <GlassListItem
                                    key={optionKey(option.props.value)}
                                    label={option.props.label}
                                    active={index === activeIndex}
                                    disabled={option.props.disabled}
                                    {...getItemProps({ onClick: () => handleSelect(option.props) })}
                                 >
                                    {option}
                                 </GlassListItem>
                              ))}
                           </FloatingList>
                           {!availableOptions.length && <div className="GlassOption" role="status">{Localization.getText("No more options")}</div>}
                        </div>
                     </div>
                  </FloatingFocusManager>
               </MITFloatingPortal>
            )}
         </div>
      </>
   );
}

function SelectedOption<T extends OptionValue>({ value, label, disabled, onRemove, children }: {
   value: T, label: string, disabled: boolean, onRemove: () => void, children: React.ReactNode
}) {
   const controls = useDragControls();
   return (
      <Reorder.Item as="div" value={value} dragListener={false} dragControls={controls} className="MultiElement" aria-disabled={disabled}>
         <button
            type="button"
            className="MultiDragHandle"
            disabled={disabled}
            aria-label={`${Localization.getText("Reorder")}: ${label}`}
            title={Localization.getText("Drag to reorder")}
            onPointerDown={(event) => {
               if (!disabled) controls.start(event);
            }}
         ><MIBIcon icon="dragHandle" /></button>
         {children}
         <button type="button" disabled={disabled} aria-label={`${Localization.getText("Remove")}: ${label}`} onClick={onRemove}>
            <MIBIcon icon="close" />
         </button>
      </Reorder.Item>
   );
}

export function GlassListItem({ children, active, label, disabled, ...props }: {
   children: React.ReactNode, active?: boolean, label: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
   const { ref } = useListItem({ label: disabled ? null : label });
   return (
      <button
         {...props}
         ref={ref}
         type="button"
         role="option"
         aria-selected={false}
         aria-disabled={disabled}
         disabled={disabled}
         tabIndex={active ? 0 : -1}
         className={`GlassListItem ${active ? "active" : ""}`}
      >{children}</button>
   );
}

export function AdvOption<T extends OptionValue>({ label, children }: AdvOptionProps<T>) {
   return <div className="AdvOption">{children ?? label}</div>;
}

export function GlassMultiLayerSelect(props: {
   selected: number[] | undefined,
   onSelect: (layerId: number[]) => void,
   options: { label: string, value: number, disabled?: boolean }[],
   disabled?: boolean
}) {
   return (
      <GlassInputControl>
         <GlassMultiSelect value={props.selected} onChange={props.onSelect} disabled={props.disabled} placeholder={Localization.getText("Select layer...")}>
            {props.options.map((option) =>
               <AdvOption key={option.value} label={option.label} value={option.value} disabled={option.disabled}>
                  <MIBIcon icon="layer" /><span>{option.label}</span>
               </AdvOption>
            )}
         </GlassMultiSelect>
      </GlassInputControl>
   );
}
